import React, { useState, useEffect } from 'react';
import { workspaceAPI, connectorsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/* eslint-disable */

// ─── Shared styles ───────────────────────────────────────────
const card = { background:'#fff', border:'0.5px solid rgba(0,0,0,0.07)', borderRadius:'12px', boxShadow:'0 1px 3px rgba(0,0,0,0.05)' };
const inp = { width:'100%', padding:'7px 11px', background:'#F0EDE5', border:'0.5px solid rgba(0,0,0,0.1)', borderRadius:'7px', fontSize:'12px', color:'#1A1916', outline:'none', fontFamily:'monospace' };
const btn = { background:'#B8860B', color:'#fff', border:'none', padding:'7px 18px', borderRadius:'7px', fontSize:'12px', fontWeight:500, cursor:'pointer', fontFamily:"'DM Sans', sans-serif" };
const btnGhost = { background:'transparent', border:'0.5px solid rgba(0,0,0,0.12)', padding:'7px 16px', borderRadius:'7px', fontSize:'12px', cursor:'pointer', color:'rgba(26,25,22,0.55)', fontFamily:"'DM Sans', sans-serif" };

function Field({ label, placeholder, value, onChange, type='text', hint, mono=true }) {
  return (
    <div style={{ marginBottom:'10px' }}>
      <div style={{ fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.07em', color:'rgba(26,25,22,0.35)', marginBottom:'5px' }}>{label}</div>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ ...inp, fontFamily: mono ? 'monospace' : "'DM Sans', sans-serif" }} />
      {hint && <div style={{ fontSize:'10px', color:'rgba(26,25,22,0.38)', marginTop:'3px' }}>{hint}</div>}
    </div>
  );
}

function Toast({ msg, type='success' }) {
  if (!msg) return null;
  return (
    <div style={{ position:'fixed', bottom:'1.5rem', right:'1.5rem', background: type==='error'?'#B03020':'#1A7A4A', color:'#fff', padding:'10px 18px', borderRadius:'8px', fontSize:'13px', zIndex:9999, boxShadow:'0 4px 16px rgba(0,0,0,0.15)' }}>
      {msg}
    </div>
  );
}

// ─── Connector Card (summary row) ─────────────────────────────
function ConnectorRow({ title, icon, desc, connected, onConfigure, webhookInfo }) {
  return (
    <div style={{ ...card, padding:'14px 18px', display:'flex', alignItems:'center', gap:'14px', marginBottom:'8px' }}>
      <div style={{ fontSize:'22px', flexShrink:0 }}>{icon}</div>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:'13px', fontWeight:500, color:'#1A1916', marginBottom:'2px' }}>{title}</div>
        <div style={{ fontSize:'11px', color:'rgba(26,25,22,0.45)' }}>{desc}</div>
        {webhookInfo && <div style={{ fontSize:'10px', marginTop:'3px', color: webhookInfo.color || 'rgba(26,25,22,0.35)' }}>{webhookInfo.text}</div>}
      </div>
      <span style={{
        fontSize:'11px', fontWeight:500, padding:'2px 9px', borderRadius:'4px',
        background: connected ? 'rgba(26,122,74,0.08)' : 'rgba(0,0,0,0.05)',
        color: connected ? '#1A7A4A' : 'rgba(26,25,22,0.4)',
      }}>
        {connected ? '✓ Connecté' : 'Non connecté'}
      </span>
      <button onClick={onConfigure} style={connected ? btnGhost : btn}>
        {connected ? 'Reconfigurer' : 'Configurer'}
      </button>
    </div>
  );
}

// ─── Step indicator ──────────────────────────────────────────
function Steps({ steps, current }) {
  return (
    <div style={{ display:'flex', gap:'4px', marginBottom:'1.25rem' }}>
      {steps.map((s, i) => (
        <div key={i} style={{ flex:1, textAlign:'center' }}>
          <div style={{
            height:'3px', borderRadius:'2px', marginBottom:'6px',
            background: i <= current ? '#B8860B' : 'rgba(0,0,0,0.08)',
          }} />
          <div style={{ fontSize:'10px', color: i <= current ? '#B8860B' : 'rgba(26,25,22,0.3)', fontWeight: i === current ? 600 : 400 }}>{s}</div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// AIRTABLE WIZARD
// ═══════════════════════════════════════════════════════════════
function AirtableWizard({ connector, onDone, onClose, showToast }) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testOk, setTestOk] = useState(false);
  const [tables, setTables] = useState([]);
  const [columns, setColumns] = useState([]);
  const [config, setConfig] = useState({
    api_key:'', base_id:'', table_id:'',
    field_mapping:{}, status_mapping:{}, value_mappings:{}, attachments_mapping:{},
    sync_direction:'bidirectional', create_new_dossiers:true,
  });
  // Reconfigure mode: connector already exists, PAT is masked server-side
  const isReconfigure = !!(connector && connector.id);
  const [editPat, setEditPat] = useState(false);
  const set = (k, v) => setConfig(p => ({ ...p, [k]: v }));

  // Hydrate state from existing connector on open
  useEffect(() => {
    if (!connector) return;
    const c = connector.config || {};
    setConfig(p => ({
      ...p,
      api_key: '',  // never returned by API; only set if user types a new one
      base_id: c.base_id || '',
      table_id: c.table_id || '',
      field_mapping: c.field_mapping || {},
      status_mapping: c.status_mapping || {},
      value_mappings: c.value_mappings || {},
      attachments_mapping: c.attachments_mapping || {},
      sync_direction: c.sync_direction || 'bidirectional',
      create_new_dossiers: c.create_new_dossiers !== false,
    }));
    setTestOk(true);
    // Load tables + columns silently using stored PAT (so Mapping step works)
    connectorsAPI.listAirtableTables()
      .then(r => {
        if (r.data?.success) {
          const tbls = r.data.tables || [];
          setTables(tbls);
          const sel = tbls.find(t => (t.id || t.name) === (c.table_id || ''));
          if (sel) setColumns(sel.fields || []);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connector?.id]);

  const handleTest = async () => {
    setLoading(true); setError('');
    try {
      const r = await connectorsAPI.testAirtable({ api_key: config.api_key, base_id: config.base_id });
      if (r.data?.success) {
        setTestOk(true);
        setTables(r.data.tables || []);
        if (r.data.tables?.length === 1) {
          set('table_id', r.data.tables[0].id || r.data.tables[0].name);
          setColumns(r.data.tables[0].fields || []);
        }
      } else {
        setError(r.data?.error || 'Échec de la connexion');
      }
    } catch (e) { setError(e.response?.data?.detail || 'Erreur réseau'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setLoading(true); setError('');
    try {
      const payload = { ...config };
      if (!payload.api_key) delete payload.api_key;  // reconfigure: keep stored PAT server-side
      await connectorsAPI.saveAirtable(payload);
      try {
        const wh = await connectorsAPI.setupAirtableWebhook();
        if (wh.data?.success) { showToast('Airtable connecté + webhook auto'); }
        else { showToast('Airtable connecté (webhook manuel requis)'); }
      } catch { showToast('Airtable connecté (webhook auto indisponible)'); }
      onDone();
    } catch (e) { setError(e.response?.data?.detail || 'Erreur lors de la sauvegarde'); }
    finally { setLoading(false); }
  };

  const FIELD_SECTIONS = [
    { title:'Identité', fields:[
      { key:'societe_nom', label:'Nom société', aliases:['nom','denomination','societe','raison sociale'] },
      { key:'societe_siren', label:'SIREN', aliases:['siren','siret','rcs'] },
      { key:'reference', label:'Référence', aliases:['ref','reference','numero'] },
    ]},
    { title:'Formalité', fields:[
      { key:'type', label:'Type', aliases:['type','formalite','nature'] },
      { key:'status', label:'Statut', aliases:['statut','status','etat'] },
      { key:'priorite', label:'Priorité', aliases:['priorite','urgence'] },
    ]},
    { title:'Intervenants', fields:[
      { key:'formaliste_airtable', label:'Formaliste', aliases:['formaliste','assignee','responsable'] },
      { key:'btob_cabinet', label:'Cabinet BtoB', aliases:['btob','cabinet','partenaire'] },
      { key:'greffe', label:'Greffe', aliases:['greffe','tribunal'] },
      { key:'commercial', label:'Commercial', aliases:['commercial','vendeur'] },
    ]},
    { title:'Contact', fields:[
      { key:'email_client_final', label:'Email client', aliases:['email','mail','courriel'] },
      { key:'telephone_client_final', label:'Téléphone', aliases:['telephone','tel','phone','mobile'] },
    ]},
    { title:'Suivi', fields:[
      { key:'elements_manquants', label:'Éléments manquants', aliases:['manquant','missing','a fournir'] },
      { key:'etat_facturation', label:'Facturation', aliases:['facturation','facture','billing'] },
    ]},
    { title:'Lexback', fields:[
      { key:'debours_lexback', label:'Débours', aliases:['debours','frais'] },
      { key:'notes_formaliste_lexback', label:'Notes', aliases:['notes','commentaire'] },
    ]},
  ];
  const normalize = s => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').replace(/[^a-z0-9]/g,'');
  const getVal = k => { const v = config.field_mapping[k]; return !v ? '' : typeof v === 'string' ? v : v.airtable_col || ''; };
  const getMode = k => { const v = config.field_mapping[k]; return !v || typeof v === 'string' ? 'bidirectional' : v.mode || 'bidirectional'; };
  const setField = (k, col) => set('field_mapping', {...config.field_mapping, [k]:{ airtable_col:col, mode:getMode(k) }});
  const setMode = (k, mode) => set('field_mapping', {...config.field_mapping, [k]:{ airtable_col:getVal(k), mode }});
  const autoDetect = () => {
    const auto = {};
    FIELD_SECTIONS.forEach(sec => sec.fields.forEach(f => {
      const aliases = [f.key.replace(/_/g,' '), ...f.aliases];
      const match = columns.find(c => { const cn = normalize(c.name); return aliases.some(a => cn.includes(normalize(a))); });
      if (match) auto[f.key] = { airtable_col:match.name, mode: sec.title === 'Lexback' ? 'lexback_only' : 'bidirectional' };
    }));
    set('field_mapping', {...config.field_mapping, ...auto});
  };

  const STEPS = ['Connexion', 'Mapping champs', 'Mapping valeurs', 'Pièces jointes', 'Activation'];

  const ENUM_FIELDS = [
    { key: 'status', label: 'Statut', options: [
      {value:'recu',label:'Reçu'},{value:'assigne',label:'Assigné'},{value:'en_cours',label:'En cours'},
      {value:'depose',label:'Déposé'},{value:'termine',label:'Terminé'},{value:'rejete',label:'Rejeté'},
      {value:'doc_manquant',label:'Doc manquant'}
    ]},
    { key: 'type', label: 'Type de formalité', options: [
      {value:'creation_sas',label:'Création SAS'},{value:'creation_sarl',label:'Création SARL'},
      {value:'creation_sasu',label:'Création SASU'},{value:'creation_eurl',label:'Création EURL'},
      {value:'creation_sci',label:'Création SCI'},{value:'creation_ei',label:'Création EI'},
      {value:'modification',label:'Modification'},{value:'dissolution',label:'Dissolution'},
      {value:'radiation',label:'Radiation'},{value:'transfert_siege',label:'Transfert siège'},
      {value:'depot_comptes',label:'Dépôt comptes'},{value:'autre',label:'Autre'}
    ]},
  ];

  const setValueMapping = (field, airtableVal, lexbackVal) => {
    setConfig(p => ({
      ...p,
      value_mappings: {
        ...p.value_mappings,
        [field]: { ...(p.value_mappings?.[field] || {}), [airtableVal]: lexbackVal }
      }
    }));
  };
  const labelStyle = { fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.07em', color:'rgba(26,25,22,0.35)', marginBottom:'5px' };

  return (
    <div style={{ ...card, padding:'1.5rem' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
        <div style={{ fontFamily:'Syne, sans-serif', fontSize:'14px', fontWeight:600, color:'#1A1916' }}>Configuration Airtable</div>
        <button onClick={onClose} style={{ ...btnGhost, padding:'4px 10px', fontSize:'11px' }}>Fermer</button>
      </div>
      <Steps steps={STEPS} current={step} />

      {error && <div style={{ background:'rgba(176,48,32,0.06)', border:'0.5px solid rgba(176,48,32,0.2)', borderRadius:'6px', padding:'8px 12px', fontSize:'11px', color:'#B03020', marginBottom:'12px' }}>{error}</div>}

      {/* Step 0: Connection */}
      {step === 0 && (
        <div>
          {isReconfigure && !editPat ? (
            <>
              <div style={{ marginBottom:'10px' }}>
                <div style={labelStyle}>Personal Access Token</div>
                <div style={{ ...inp, display:'flex', alignItems:'center', justifyContent:'space-between', color:'rgba(26,25,22,0.55)', fontFamily:'monospace', cursor:'default' }}>
                  <span>pat••••••••••••••••</span>
                  <button onClick={() => { setEditPat(true); setTestOk(false); }} style={{ background:'none', border:'none', color:'#B8860B', fontSize:'10px', cursor:'pointer', textDecoration:'underline' }}>Modifier</button>
                </div>
              </div>
              <div style={{ marginBottom:'10px' }}>
                <div style={labelStyle}>Base ID</div>
                <div style={{ ...inp, color:'rgba(26,25,22,0.7)', fontFamily:'monospace', cursor:'default' }}>{config.base_id || '—'}</div>
              </div>
              {tables.length > 0 && (
                <div style={{ marginBottom:'10px' }}>
                  <div style={labelStyle}>Table cible</div>
                  <select value={config.table_id} onChange={e => { set('table_id', e.target.value); const t = tables.find(t => (t.id||t.name) === e.target.value); setColumns(t ? t.fields||[] : []); }} style={{ ...inp, fontFamily:"'DM Sans', sans-serif" }}>
                    {tables.map(t => <option key={t.id||t.name} value={t.id||t.name}>{t.name}</option>)}
                  </select>
                </div>
              )}
              <div style={{ background:'rgba(26,122,74,0.06)', border:'0.5px solid rgba(26,122,74,0.2)', borderRadius:'6px', padding:'8px 12px', fontSize:'11px', color:'#1A7A4A', marginBottom:'12px' }}>
                Connexion existante — passez directement aux étapes suivantes
              </div>
              <div style={{ display:'flex', gap:'8px', marginTop:'8px' }}>
                <button onClick={() => setStep(1)} disabled={!config.table_id} style={{ ...btn, opacity: !config.table_id ? 0.5 : 1 }}>Suivant</button>
              </div>
            </>
          ) : (
            <>
              <Field label="Personal Access Token" placeholder="patXXXXXXXXXXXXXX" value={config.api_key} onChange={v => set('api_key', v)} hint="Airtable → Developer hub → Personal access tokens" />
              <Field label="Base ID" placeholder="appXXXXXXXXXXXXXX" value={config.base_id} onChange={v => set('base_id', v)} hint="URL de votre base : airtable.com/appXXXXX/..." />
              {testOk && <div style={{ background:'rgba(26,122,74,0.06)', border:'0.5px solid rgba(26,122,74,0.2)', borderRadius:'6px', padding:'8px 12px', fontSize:'11px', color:'#1A7A4A', marginBottom:'12px' }}>Connexion OK — {tables.length} table(s)</div>}
              {testOk && tables.length > 0 && (
                <div style={{ marginBottom:'10px' }}>
                  <div style={labelStyle}>Table cible</div>
                  <select value={config.table_id} onChange={e => { set('table_id', e.target.value); const t = tables.find(t => (t.id||t.name) === e.target.value); setColumns(t ? t.fields||[] : []); }} style={{ ...inp, fontFamily:"'DM Sans', sans-serif" }}>
                    <option value="">— Sélectionner —</option>
                    {tables.map(t => <option key={t.id||t.name} value={t.id||t.name}>{t.name}</option>)}
                  </select>
                </div>
              )}
              <div style={{ display:'flex', gap:'8px', marginTop:'8px' }}>
                <button onClick={handleTest} disabled={loading || !config.api_key || !config.base_id} style={{ ...btn, opacity: loading || !config.api_key || !config.base_id ? 0.5 : 1 }}>
                  {loading ? 'Test...' : 'Tester la connexion'}
                </button>
                {testOk && config.table_id && <button onClick={() => setStep(1)} style={btn}>Suivant</button>}
                {isReconfigure && <button onClick={() => { setEditPat(false); setTestOk(true); }} style={btnGhost}>Annuler</button>}
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 1: Field mapping */}
      {step === 1 && (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
            <div style={{ fontSize:'11px', color:'rgba(26,25,22,0.55)' }}>Associez chaque champ à une colonne Airtable</div>
            <button onClick={autoDetect} style={{ ...btnGhost, fontSize:'10px', padding:'3px 8px' }}>Auto-détecter</button>
          </div>
          {FIELD_SECTIONS.map(sec => (
            <div key={sec.title} style={{ marginBottom:'10px' }}>
              <div style={{ fontSize:'9px', fontWeight:600, color:'#B8860B', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px' }}>{sec.title}</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                {sec.fields.map(f => (
                  <div key={f.key} style={{ display:'flex', gap:'4px', alignItems:'flex-end' }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:'10px', color:'rgba(26,25,22,0.45)', marginBottom:'2px' }}>{f.label}</div>
                      <select value={getVal(f.key)} onChange={e => setField(f.key, e.target.value)} style={{ ...inp, fontSize:'10px' }}>
                        <option value="">— Non mappé —</option>
                        {columns.map(col => <option key={col.id||col.name} value={col.name}>{col.name} ({col.type})</option>)}
                      </select>
                    </div>
                    {getVal(f.key) && (
                      <select value={getMode(f.key)} onChange={e => setMode(f.key, e.target.value)} style={{ ...inp, width:'75px', fontSize:'9px', padding:'5px 3px' }}>
                        <option value="bidirectional">Bidir</option>
                        <option value="read_only">Lecture</option>
                        <option value="lexback_only">Lexback</option>
                      </select>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={() => setStep(0)} style={btnGhost}>Retour</button>
            <button onClick={() => setStep(2)} style={btn}>Suivant</button>
          </div>
        </div>
      )}

      {/* Step 2: Value mapping + options */}
      {step === 2 && (
        <div>
          <div style={{ fontSize:'11px', color:'rgba(26,25,22,0.55)', marginBottom:'10px' }}>Mappez les valeurs Airtable vers les valeurs Lexback pour les champs à choix.</div>
          {ENUM_FIELDS.map(ef => {
            const mappedCol = getVal(ef.key);
            const col = columns.find(c => c.name === mappedCol);
            if (!mappedCol || !col?.choices) return null;
            return (
              <div key={ef.key} style={{ marginBottom:'14px' }}>
                <div style={{ fontSize:'11px', fontWeight:600, color:'#B8860B', marginBottom:'6px' }}>{ef.label} ({mappedCol})</div>
                <div style={{ display:'grid', gap:'4px' }}>
                  {col.choices.map(choice => (
                    <div key={choice.name} style={{ display:'flex', alignItems:'center', gap:'8px', background:'#F5F3EE', borderRadius:'6px', padding:'6px 10px' }}>
                      <span style={{ flex:1, fontSize:'11px', color:'#1A1916' }}>{choice.name}</span>
                      <select value={config.value_mappings?.[ef.key]?.[choice.name] || ''}
                        onChange={e => setValueMapping(ef.key, choice.name, e.target.value)}
                        style={{ ...inp, width:'140px', fontSize:'10px' }}>
                        <option value="">— Non mappé —</option>
                        {ef.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {ENUM_FIELDS.every(ef => { const mc = getVal(ef.key); const col = columns.find(c => c.name === mc); return !mc || !col?.choices; }) && (
            <div style={{ background:'#F5F3EE', borderRadius:'6px', padding:'12px', fontSize:'11px', color:'rgba(26,25,22,0.45)', marginBottom:'12px' }}>
              Aucun champ singleSelect mappé sur « type » ou « status ». Vous pouvez passer à l'étape suivante.
            </div>
          )}
          <div style={labelStyle}>Options</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'12px' }}>
            <div>
              <div style={{ fontSize:'10px', color:'rgba(26,25,22,0.45)', marginBottom:'2px' }}>Direction sync</div>
              <select value={config.sync_direction} onChange={e => set('sync_direction', e.target.value)} style={{ ...inp, fontFamily:"'DM Sans', sans-serif" }}>
                <option value="bidirectional">Bidirectionnelle</option>
                <option value="outbound_only">Sortante seule</option>
                <option value="inbound_only">Entrante seule</option>
              </select>
            </div>
            <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'11px', color:'#1A1916', cursor:'pointer', paddingTop:'14px' }}>
              <input type="checkbox" checked={config.create_new_dossiers} onChange={e => set('create_new_dossiers', e.target.checked)} />
              Créer dossiers auto
            </label>
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={() => setStep(1)} style={btnGhost}>Retour</button>
            <button onClick={() => setStep(3)} style={btn}>Suivant</button>
          </div>
        </div>
      )}

      {/* Step 3: Attachments */}
      {step === 3 && (() => {
        const attachCols = columns.filter(c => c.type === 'multipleAttachments');
        const cats = [{value:'justificative',label:'Justificative'},{value:'juridique',label:'Juridique'},{value:'officiel',label:'Officiel'},{value:'lexback_internal',label:'Lexback interne'}];
        return (
          <div>
            <div style={{ fontSize:'11px', color:'rgba(26,25,22,0.55)', marginBottom:'10px' }}>Mappez les colonnes contenant des pièces jointes vers des catégories.</div>
            {attachCols.length === 0 ? (
              <div style={{ background:'#F5F3EE', borderRadius:'6px', padding:'12px', fontSize:'11px', color:'rgba(26,25,22,0.45)', marginBottom:'12px' }}>Aucune colonne "Attachments" détectée.</div>
            ) : (
              <div style={{ display:'grid', gap:'6px', marginBottom:'12px' }}>
                {attachCols.map(col => (
                  <div key={col.name} style={{ display:'flex', gap:'8px', alignItems:'center', background:'#F5F3EE', borderRadius:'6px', padding:'8px 12px' }}>
                    <div style={{ flex:1, fontSize:'11px', fontWeight:500, color:'#1A1916' }}>{col.name}</div>
                    <select value={(config.attachments_mapping[col.name]||{}).category||''} onChange={e => set('attachments_mapping', {...config.attachments_mapping, [col.name]:{airtable_col:col.name, category:e.target.value}})} style={{ ...inp, width:'130px', fontSize:'10px' }}>
                      <option value="">— Ignorer —</option>
                      {cats.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display:'flex', gap:'8px' }}>
              <button onClick={() => setStep(2)} style={btnGhost}>Retour</button>
              <button onClick={() => setStep(4)} style={btn}>Suivant</button>
            </div>
          </div>
        );
      })()}

      {/* Step 4: Activation */}
      {step === 4 && (() => {
        const mapped = Object.values(config.field_mapping).filter(v => (typeof v === 'string' ? v : v?.airtable_col)).length;
        const attachCount = Object.values(config.attachments_mapping).filter(v => v?.category).length;
        return (
          <div>
            <div style={{ background:'#F5F3EE', borderRadius:'8px', padding:'14px', marginBottom:'14px' }}>
              <div style={{ fontSize:'11px', fontWeight:600, color:'#1A1916', marginBottom:'8px' }}>Récapitulatif</div>
              {[
                ['Base ID', config.base_id],
                ['Table', config.table_id],
                ['Direction', config.sync_direction],
                ['Champs mappés', String(mapped)],
                ['Créer auto', config.create_new_dossiers ? 'Oui' : 'Non'],
                ...(attachCount > 0 ? [['PJ mappées', `${attachCount} col.`]] : []),
              ].map(([k,v]) => (
                <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', padding:'3px 0' }}>
                  <span style={{ color:'rgba(26,25,22,0.45)' }}>{k}</span>
                  <span style={{ color:'#1A1916', fontWeight:500, fontFamily:'monospace' }}>{v}</span>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', gap:'8px' }}>
              <button onClick={() => setStep(3)} style={btnGhost}>Retour</button>
              <button onClick={handleSave} disabled={loading} style={{ ...btn, opacity:loading?0.5:1 }}>
                {loading ? 'Activation...' : 'Activer Airtable'}
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ZENDESK WIZARD
// ═══════════════════════════════════════════════════════════════
function ZendeskWizard({ connector, onDone, onClose, showToast }) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testOk, setTestOk] = useState(false);
  const [groups, setGroups] = useState([]);
  const [agents, setAgents] = useState([]);
  const [config, setConfig] = useState({
    subdomain:'', email_auth:'', api_token:'',
    zendesk_filter:{}, agents_mode:'individual', formaliste_mapping:{},
    signature_template:'Cordialement,\n{{formaliste_prenom}} {{formaliste_nom_initiale}}\n{{signature_suffix}}',
    signature_suffix:'', white_label_mode:true,
    field_mapping:{}, status_mapping:{}, sync_direction:'bidirectional',
    create_dossier_on_new_ticket:false,
  });
  const set = (k, v) => setConfig(p => ({ ...p, [k]: v }));

  const creds = { subdomain: config.subdomain, email_auth: config.email_auth, api_token: config.api_token };

  const handleTest = async () => {
    setLoading(true); setError('');
    try {
      const r = await connectorsAPI.testZendesk(creds);
      if (r.data?.success) {
        setTestOk(true);
        const [g, a] = await Promise.all([
          connectorsAPI.zendeskListGroups(creds),
          connectorsAPI.zendeskListAgents(creds),
        ]);
        setGroups(g.data?.groups || []);
        setAgents(a.data?.agents || []);
      } else { setError(r.data?.error || 'Échec'); }
    } catch (e) { setError(e.response?.data?.detail || 'Erreur réseau'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setLoading(true); setError('');
    try {
      await connectorsAPI.saveZendesk(config);
      showToast('Zendesk connecté ✓');
      onDone();
    } catch (e) { setError(e.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  };

  const STEPS = ['Connexion', 'Filtrage', 'Signature', 'Activation'];

  return (
    <div style={{ ...card, padding:'1.5rem' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
        <div style={{ fontFamily:'Syne, sans-serif', fontSize:'14px', fontWeight:600, color:'#1A1916' }}>💬 Configuration Zendesk</div>
        <button onClick={onClose} style={{ ...btnGhost, padding:'4px 10px', fontSize:'11px' }}>✕ Fermer</button>
      </div>
      <Steps steps={STEPS} current={step} />
      {error && <div style={{ background:'rgba(176,48,32,0.06)', border:'0.5px solid rgba(176,48,32,0.2)', borderRadius:'6px', padding:'8px 12px', fontSize:'11px', color:'#B03020', marginBottom:'12px' }}>{error}</div>}

      {step === 0 && (
        <div>
          <Field label="Sous-domaine Zendesk" placeholder="monentreprise" value={config.subdomain} onChange={v => set('subdomain', v)} hint="monentreprise.zendesk.com → saisissez 'monentreprise'" mono={false} />
          <Field label="Email admin Zendesk" placeholder="admin@monentreprise.com" value={config.email_auth} onChange={v => set('email_auth', v)} mono={false} />
          <Field label="API Token" placeholder="••••••••" value={config.api_token} onChange={v => set('api_token', v)} hint="Admin → Apps & Integrations → API" />
          {testOk && <div style={{ background:'rgba(26,122,74,0.06)', border:'0.5px solid rgba(26,122,74,0.2)', borderRadius:'6px', padding:'8px 12px', fontSize:'11px', color:'#1A7A4A', marginBottom:'12px' }}>✓ Connexion réussie — {groups.length} groupe(s), {agents.length} agent(s)</div>}
          <div style={{ display:'flex', gap:'8px', marginTop:'8px' }}>
            <button onClick={handleTest} disabled={loading || !config.subdomain || !config.email_auth || !config.api_token} style={{ ...btn, opacity: loading || !config.subdomain ? 0.5 : 1 }}>
              {loading ? 'Test…' : 'Tester la connexion'}
            </button>
            {testOk && <button onClick={() => setStep(1)} style={btn}>Suivant →</button>}
          </div>
        </div>
      )}

      {step === 1 && (
        <div>
          <div style={{ fontSize:'12px', color:'rgba(26,25,22,0.55)', marginBottom:'12px', lineHeight:1.6 }}>
            Filtrez les tickets à synchroniser. Laissez vide pour tout synchroniser.
          </div>
          <div style={{ marginBottom:'10px' }}>
            <div style={{ fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.07em', color:'rgba(26,25,22,0.35)', marginBottom:'5px' }}>Mode agents formalistes</div>
            <select value={config.agents_mode} onChange={e => set('agents_mode', e.target.value)} style={{ ...inp, fontFamily:"'DM Sans', sans-serif" }}>
              <option value="individual">Individuel — chaque formaliste a son profil Zendesk</option>
              <option value="shared">Partagé — un seul compte Zendesk pour tous</option>
            </select>
          </div>
          <label style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'12px', color:'#1A1916', cursor:'pointer', marginBottom:'12px' }}>
            <input type="checkbox" checked={config.create_dossier_on_new_ticket} onChange={e => set('create_dossier_on_new_ticket', e.target.checked)} />
            Créer un dossier Lexback pour chaque nouveau ticket
          </label>
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={() => setStep(0)} style={btnGhost}>← Retour</button>
            <button onClick={() => setStep(2)} style={btn}>Suivant →</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <div style={{ fontSize:'12px', color:'rgba(26,25,22,0.55)', marginBottom:'12px', lineHeight:1.6 }}>
            Personnalisez la signature des réponses Zendesk envoyées par vos formalistes.
          </div>
          <label style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'12px', color:'#1A1916', cursor:'pointer', marginBottom:'12px' }}>
            <input type="checkbox" checked={config.white_label_mode} onChange={e => set('white_label_mode', e.target.checked)} />
            Mode white-label (aucune mention Lexback dans les emails)
          </label>
          <Field label="Suffixe signature" placeholder="— Service Juridique" value={config.signature_suffix} onChange={v => set('signature_suffix', v)} mono={false} hint="Ajouté après le nom du formaliste" />
          <div style={{ background:'#F5F3EE', borderRadius:'8px', padding:'10px 14px', marginBottom:'14px' }}>
            <div style={{ fontSize:'10px', color:'rgba(26,25,22,0.35)', marginBottom:'4px' }}>Aperçu signature</div>
            <div style={{ fontSize:'12px', color:'#1A1916', whiteSpace:'pre-line', lineHeight:1.6 }}>
              Cordialement,{'\n'}Marie D.{config.signature_suffix ? '\n' + config.signature_suffix : ''}
            </div>
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={() => setStep(1)} style={btnGhost}>← Retour</button>
            <button onClick={() => setStep(3)} style={btn}>Suivant →</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          <div style={{ background:'#F5F3EE', borderRadius:'8px', padding:'14px', marginBottom:'14px' }}>
            <div style={{ fontSize:'11px', fontWeight:600, color:'#1A1916', marginBottom:'8px' }}>Récapitulatif</div>
            {[
              ['Sous-domaine', config.subdomain],
              ['Email', config.email_auth],
              ['Agents', config.agents_mode],
              ['White-label', config.white_label_mode ? 'Oui' : 'Non'],
              ['Créer dossiers auto', config.create_dossier_on_new_ticket ? 'Oui' : 'Non'],
            ].map(([k,v]) => (
              <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', padding:'3px 0' }}>
                <span style={{ color:'rgba(26,25,22,0.45)' }}>{k}</span>
                <span style={{ color:'#1A1916', fontWeight:500 }}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={() => setStep(2)} style={btnGhost}>← Retour</button>
            <button onClick={handleSave} disabled={loading} style={{ ...btn, opacity:loading?0.5:1 }}>
              {loading ? 'Activation…' : 'Activer Zendesk ✓'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// AIRCALL WIZARD
// ═══════════════════════════════════════════════════════════════
function AircallWizard({ connector, onDone, onClose, showToast }) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testOk, setTestOk] = useState(false);
  const [numbers, setNumbers] = useState([]);
  const [config, setConfig] = useState({
    api_id:'', api_token:'', webhook_token:'',
    aircall_filter:{}, enrich_existing_dossiers:true,
    create_dossier_on_call:false, match_by:['phone','email'],
  });
  const set = (k, v) => setConfig(p => ({ ...p, [k]: v }));

  const handleTest = async () => {
    setLoading(true); setError('');
    try {
      const r = await connectorsAPI.testAircall({ api_id: config.api_id, api_token: config.api_token });
      if (r.data?.success) {
        setTestOk(true);
        const n = await connectorsAPI.aircallListNumbers({ api_id: config.api_id, api_token: config.api_token });
        setNumbers(n.data?.numbers || []);
      } else { setError(r.data?.error || 'Échec'); }
    } catch (e) { setError(e.response?.data?.detail || 'Erreur réseau'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setLoading(true); setError('');
    try {
      await connectorsAPI.saveAircall(config);
      showToast('Aircall connecté ✓');
      onDone();
    } catch (e) { setError(e.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  };

  const STEPS = ['Connexion', 'Matching', 'Activation'];

  return (
    <div style={{ ...card, padding:'1.5rem' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
        <div style={{ fontFamily:'Syne, sans-serif', fontSize:'14px', fontWeight:600, color:'#1A1916' }}>📞 Configuration Aircall</div>
        <button onClick={onClose} style={{ ...btnGhost, padding:'4px 10px', fontSize:'11px' }}>✕ Fermer</button>
      </div>
      <Steps steps={STEPS} current={step} />
      {error && <div style={{ background:'rgba(176,48,32,0.06)', border:'0.5px solid rgba(176,48,32,0.2)', borderRadius:'6px', padding:'8px 12px', fontSize:'11px', color:'#B03020', marginBottom:'12px' }}>{error}</div>}

      {step === 0 && (
        <div>
          <Field label="API ID" placeholder="xxxxxxxx" value={config.api_id} onChange={v => set('api_id', v)} hint="Aircall → Settings → API keys" />
          <Field label="API Token" placeholder="••••••••" value={config.api_token} onChange={v => set('api_token', v)} />
          {testOk && <div style={{ background:'rgba(26,122,74,0.06)', border:'0.5px solid rgba(26,122,74,0.2)', borderRadius:'6px', padding:'8px 12px', fontSize:'11px', color:'#1A7A4A', marginBottom:'12px' }}>✓ Connexion réussie — {numbers.length} numéro(s)</div>}
          <div style={{ display:'flex', gap:'8px', marginTop:'8px' }}>
            <button onClick={handleTest} disabled={loading || !config.api_id || !config.api_token} style={{ ...btn, opacity: loading || !config.api_id ? 0.5 : 1 }}>
              {loading ? 'Test…' : 'Tester la connexion'}
            </button>
            {testOk && <button onClick={() => setStep(1)} style={btn}>Suivant →</button>}
          </div>
        </div>
      )}

      {step === 1 && (
        <div>
          <div style={{ fontSize:'12px', color:'rgba(26,25,22,0.55)', marginBottom:'12px', lineHeight:1.6 }}>
            Configurez comment Aircall associe les appels à vos dossiers Lexback.
          </div>
          <label style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'12px', color:'#1A1916', cursor:'pointer', marginBottom:'8px' }}>
            <input type="checkbox" checked={config.enrich_existing_dossiers} onChange={e => set('enrich_existing_dossiers', e.target.checked)} />
            Enrichir les dossiers existants avec les données d'appel
          </label>
          <label style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'12px', color:'#1A1916', cursor:'pointer', marginBottom:'12px' }}>
            <input type="checkbox" checked={config.create_dossier_on_call} onChange={e => set('create_dossier_on_call', e.target.checked)} />
            Créer un dossier pour chaque appel sans correspondance
          </label>
          <div style={{ fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.07em', color:'rgba(26,25,22,0.35)', marginBottom:'5px' }}>Critères de matching</div>
          <div style={{ display:'flex', gap:'8px', marginBottom:'12px' }}>
            {['phone','email','name'].map(m => (
              <label key={m} style={{ display:'flex', alignItems:'center', gap:'4px', fontSize:'12px', color:'#1A1916', cursor:'pointer' }}>
                <input type="checkbox" checked={config.match_by.includes(m)} onChange={e => {
                  set('match_by', e.target.checked ? [...config.match_by, m] : config.match_by.filter(x => x !== m));
                }} />
                {m === 'phone' ? 'Téléphone' : m === 'email' ? 'Email' : 'Nom'}
              </label>
            ))}
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={() => setStep(0)} style={btnGhost}>← Retour</button>
            <button onClick={() => setStep(2)} style={btn}>Suivant →</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <div style={{ background:'#F5F3EE', borderRadius:'8px', padding:'14px', marginBottom:'14px' }}>
            <div style={{ fontSize:'11px', fontWeight:600, color:'#1A1916', marginBottom:'8px' }}>Récapitulatif</div>
            {[
              ['API ID', config.api_id],
              ['Enrichir dossiers', config.enrich_existing_dossiers ? 'Oui' : 'Non'],
              ['Créer dossiers auto', config.create_dossier_on_call ? 'Oui' : 'Non'],
              ['Matching', config.match_by.join(', ')],
            ].map(([k,v]) => (
              <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', padding:'3px 0' }}>
                <span style={{ color:'rgba(26,25,22,0.45)' }}>{k}</span>
                <span style={{ color:'#1A1916', fontWeight:500, fontFamily:'monospace' }}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={() => setStep(1)} style={btnGhost}>← Retour</button>
            <button onClick={handleSave} disabled={loading} style={{ ...btn, opacity:loading?0.5:1 }}>
              {loading ? 'Activation…' : 'Activer Aircall ✓'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CONNECTEURS TAB (main)
// ═══════════════════════════════════════════════════════════════
function ConnecteursTab({ plan }) {
  const [connectors, setConnectors] = useState([]);
  const [activeWizard, setActiveWizard] = useState(null); // 'airtable' | 'zendesk' | 'aircall' | null
  const [toast, setToast] = useState(null);

  const loadConnectors = () => {
    workspaceAPI.connectors()
      .then(r => setConnectors(r.data || []))
      .catch(() => {});
  };

  useEffect(() => { loadConnectors(); }, []);

  const isConnected = (type) => {
    const c = connectors.find(c => c.type === type);
    return c && c.is_active;
  };

  const getConnector = (type) => connectors.find(c => c.type === type) || null;

  const showToast = (msg, type='success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleDone = () => {
    setActiveWizard(null);
    loadConnectors();
  };

  const planLevel = { starter:1, pro:2, enterprise:3 }[plan] || 1;

  return (
    <div>
      {/* Active wizard */}
      {activeWizard === 'airtable' && (
        <AirtableWizard connector={getConnector('airtable')} onDone={handleDone} onClose={() => setActiveWizard(null)} showToast={showToast} />
      )}
      {activeWizard === 'zendesk' && (
        <ZendeskWizard connector={getConnector('zendesk')} onDone={handleDone} onClose={() => setActiveWizard(null)} showToast={showToast} />
      )}
      {activeWizard === 'aircall' && (
        <AircallWizard connector={getConnector('aircall')} onDone={handleDone} onClose={() => setActiveWizard(null)} showToast={showToast} />
      )}

      {/* Connectors list (hidden when wizard open) */}
      {!activeWizard && (
        <>
          <div style={{ fontSize:'11px', textTransform:'uppercase', letterSpacing:'0.08em', color:'rgba(26,25,22,0.35)', marginBottom:'10px' }}>
            Inclus automatiquement
          </div>
          <ConnectorRow title="Interface Lexback" icon="🏛️" desc="Saisie manuelle et import web" connected={true} onConfigure={() => {}} />
          <ConnectorRow title="Pappers API" icon="🔍" desc="Enrichissement automatique via SIREN" connected={true} onConfigure={() => {}} />

          <div style={{ fontSize:'11px', textTransform:'uppercase', letterSpacing:'0.08em', color:'rgba(26,25,22,0.35)', marginTop:'1.25rem', marginBottom:'10px' }}>
            Intégrations configurables
          </div>
          <ConnectorRow title="Airtable" icon="📋" desc="Synchronisation bidirectionnelle avec vos bases" connected={isConnected('airtable')} onConfigure={() => setActiveWizard('airtable')}
            webhookInfo={(() => {
              const c = getConnector('airtable');
              if (!c?.is_active) return null;
              const st = c?.config?.airtable_webhook_status;
              const exp = c?.config?.airtable_webhook_expires_at;
              if (c?.config?.airtable_webhook_id) {
                const days = exp ? Math.ceil((new Date(exp) - new Date()) / 86400000) : '?';
                const colors = { active:'#1A7A4A', expiring_soon:'#B8860B', expired:'#C0392B' };
                return { text: `Webhook actif (expire dans ${days}j)`, color: colors[st] || '#1A7A4A' };
              }
              return { text: 'Webhook manuel', color: 'rgba(26,25,22,0.35)' };
            })()}
          />
          <ConnectorRow title="Zendesk" icon="💬" desc="Centralisation des tickets support dans vos dossiers" connected={isConnected('zendesk')} onConfigure={() => setActiveWizard('zendesk')} />
          <ConnectorRow title="Aircall" icon="📞" desc="Liaison des appels téléphoniques à vos dossiers" connected={isConnected('aircall')} onConfigure={() => setActiveWizard('aircall')} />

          {planLevel < 3 && (
            <div style={{ background:'rgba(184,134,11,0.06)', border:'0.5px solid rgba(184,134,11,0.15)', borderRadius:'8px', padding:'10px 14px', fontSize:'11px', color:'#B8860B', marginTop:'1rem', lineHeight:1.5 }}>
              💡 Le plan Enterprise débloque les connecteurs avancés et l'API webhooks.
            </div>
          )}

          <div style={{ fontSize:'11px', textTransform:'uppercase', letterSpacing:'0.08em', color:'rgba(26,25,22,0.35)', marginTop:'1.25rem', marginBottom:'10px' }}>
            Bientôt disponibles
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:'8px' }}>
            {[
              { n:'Salesforce', i:'☁️', d:'Sync CRM' },
              { n:'HubSpot', i:'🟠', d:'Sync contacts' },
              { n:'Slack', i:'💬', d:'Notifications' },
            ].map(c => (
              <div key={c.n} style={{ background:'#F5F3EE', border:'0.5px solid rgba(0,0,0,0.06)', borderRadius:'10px', padding:'11px 14px', opacity:0.6 }}>
                <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'3px' }}>
                  <span style={{ fontSize:'15px' }}>{c.i}</span>
                  <div style={{ fontSize:'12px', fontWeight:500, color:'rgba(26,25,22,0.6)' }}>{c.n}</div>
                  <span style={{ marginLeft:'auto', fontSize:'9px', background:'rgba(0,0,0,0.06)', color:'rgba(26,25,22,0.35)', borderRadius:'3px', padding:'1px 5px' }}>Bientôt</span>
                </div>
                <div style={{ fontSize:'11px', color:'rgba(26,25,22,0.4)' }}>{c.d}</div>
              </div>
            ))}
          </div>
        </>
      )}

      <Toast msg={toast?.msg} type={toast?.type} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CONFIG CONSTANTS
// ═══════════════════════════════════════════════════════════════

const ETP_OPTIONS = [
  { val: 1, label: '1 formaliste dédié', price: 1500, desc: 'Idéal jusqu\'à 100 dossiers/mois' },
  { val: 2, label: '2 formalistes dédiés', price: 2800, desc: 'Jusqu\'à 200 dossiers/mois' },
  { val: 3, label: '3 formalistes dédiés', price: 3900, desc: 'Jusqu\'à 350 dossiers/mois' },
  { val: 5, label: '5 formalistes dédiés', price: 5800, desc: 'Jusqu\'à 600 dossiers/mois' },
  { val: 8, label: '8 formalistes dédiés', price: 8400, desc: 'Volumes illimités' },
];

const AGENTS = [
  { id: 'conformite', label: 'Agent Conformité', price: 300, desc: 'Vérifie automatiquement les dossiers à l\'entrée', icon: '🔍' },
  { id: 'inpi', label: 'Agent Suivi INPI', price: 200, desc: 'Polling automatique des statuts INPI', icon: '⚙️' },
  { id: 'jal', label: 'Agent JAL', price: 150, desc: 'Génération et publication automatique des annonces légales', icon: '📰' },
  { id: 'relance', label: 'Agent Relance', price: 100, desc: 'Relance automatique des clients pour documents manquants', icon: '📩' },
  { id: 'rapport', label: 'Agent Rapport', price: 150, desc: 'Génération automatique du rapport mensuel PDF', icon: '📊' },
];

const PLANS = [
  { key: 'starter', label: 'Starter', price: 0, desc: 'Cabinets jusqu\'à 50 dossiers/mois', features: ['Dossiers + messagerie', 'Facturation simple', 'Wallet INPI'] },
  { key: 'pro', label: 'Pro', price: 200, desc: 'Structures de 50 à 200 dossiers/mois', features: ['Tout Starter +', 'Dashboard pilotage', 'Scorecard qualité', 'Rapports automatiques'] },
  { key: 'enterprise', label: 'Enterprise', price: 500, desc: 'LegalTechs et volumes importants', features: ['Tout Pro +', 'Forecasting & prévisions', 'Benchmarking', 'API & webhooks', 'Connecteurs avancés'] },
];

const SERVICE_MODELS = [
  { key: 'full_service', icon: '🏢', label: 'Full service', desc: 'Formalistes Lexback dédiés à votre compte.', pricing: 'Abonnement plateforme + ETP Lexback', formaliste_source: 'Lexback assigne ses formalistes' },
  { key: 'hybride', icon: '🔀', label: 'Hybride', desc: 'Vos formalistes internes + Lexback en renfort.', pricing: 'Abonnement plateforme + ETP renfort (-40%)', formaliste_source: 'Vos formalistes en priorité, Lexback en renfort' },
  { key: 'plateforme_only', icon: '⚙️', label: 'Plateforme only', desc: 'Vous avez vos propres formalistes.', pricing: 'Abonnement plateforme 300€/mois + débours', formaliste_source: 'Vos formalistes — champ formaliste dans votre flux' },
];

// ═══════════════════════════════════════════════════════════════
// MAIN CONFIG PAGE
// ═══════════════════════════════════════════════════════════════
export default function Config() {
  const { user } = useAuth();
  const [runbook, setRunbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('runbook');

  const plan = user?.workspace?.plan || 'starter';
  const currentEtp = user?.workspace?.etp_count || 1;

  const [selectedEtp, setSelectedEtp] = useState(currentEtp);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [serviceModel, setServiceModel] = useState('full_service');
  const [renfortEtp, setRenfortEtp] = useState(1);

  useEffect(() => {
    workspaceAPI.getRunbook().then(r => { setRunbook(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };
  const handleSave = async () => {
    setSaving(true);
    try { await workspaceAPI.updateRunbook(runbook); showToast('Runbook sauvegardé ✓'); }
    catch { showToast('Erreur lors de la sauvegarde', 'error'); }
    finally { setSaving(false); }
  };

  const plateformeOnlyPrice = 300;
  const etpPrice = serviceModel === 'plateforme_only' ? 0
    : serviceModel === 'hybride' ? Math.round((ETP_OPTIONS.find(e => e.val === renfortEtp)?.price || 0) * 0.6)
    : ETP_OPTIONS.find(e => e.val === selectedEtp)?.price || 0;
  const agentsPrice = selectedAgents.reduce((acc, id) => acc + (AGENTS.find(a => a.id === id)?.price || 0), 0);
  const planPrice = PLANS.find(p => p.key === plan)?.price || 0;
  const basePrix = serviceModel === 'plateforme_only' ? plateformeOnlyPrice : planPrice;
  const totalMensuel = basePrix + etpPrice + agentsPrice;

  if (loading) return <div style={{ padding: '2rem', color: '#6B6960' }}>Chargement...</div>;

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ fontSize: '20px', fontWeight: 500, color: '#1A1916' }}>Connecteurs</div>
        <div style={{ fontSize: '13px', color: '#6B6960', marginTop: '2px' }}>Intégrations entrantes pour synchroniser vos outils existants avec Lexback</div>
      </div>

      <ConnecteursTab plan={plan} />

      {toast && <Toast msg={toast.msg} type={toast.type} />}
    </div>
  );
}
